import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isActive = (path) => location.pathname === path;

  const navLink = (to, label) => (
    <Link
      to={to}
      className={`relative font-poppins font-600 text-sm tracking-wide transition-colors duration-200 group whitespace-nowrap
        ${isActive(to) ? 'text-primary' : 'text-gray-700 hover:text-primary'}`}
    >
      {label}
      <span className={`absolute -bottom-1 left-0 h-0.5 rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-300
        ${isActive(to) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
    </Link>
  );

  const dropdowns = {
    academics: {
      label: 'Academics',
      items: [
        { to: '/academics', icon: 'fa-graduation-cap', label: 'Academic Overview' },
        { to: '/syllabus', icon: 'fa-book-open', label: 'Syllabus' },
        { to: '/booklist', icon: 'fa-list-ul', label: 'Book Lists' },
        { to: '/curriculum', icon: 'fa-layer-group', label: 'Curriculum' },
        { to: '/results', icon: 'fa-chart-bar', label: 'Results' },
        { to: '/orientations', icon: 'fa-calendar-check', label: 'Orientations' },
      ]
    },
    activities: {
      label: 'Activities',
      items: [
        { to: '/gallery', icon: 'fa-images', label: 'Gallery' },
        { to: '/daily-media', icon: 'fa-camera', label: 'Daily Media' },
        { to: '/sports', icon: 'fa-trophy', label: 'Sports Corner' },
      ]
    }
  };

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
          ${isScrolled || menuOpen
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100'
            : 'bg-white/90 backdrop-blur-sm shadow-sm'
          }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[70px]">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group" aria-label="PAK School Home">
              <img
                src="/logo.png"
                alt="PAK School & College"
                className="h-11 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div className="hidden sm:block leading-tight">
                <div className="font-poppins font-800 text-sm text-navy leading-none">PAK School</div>
                <div className="font-inter text-xs text-gray-500 leading-none mt-0.5">&amp; College System</div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-7">
              {navLink('/', 'Home')}
              {navLink('/about', 'About Us')}

              {/* Dropdown: Academics */}
              {Object.entries(dropdowns).map(([key, dropdown]) => (
                <div key={key} className="relative">
                  <button
                    className={`flex items-center gap-1.5 font-poppins font-600 text-sm tracking-wide transition-colors duration-200 whitespace-nowrap
                      ${dropdown.items.some(i => isActive(i.to)) ? 'text-primary' : 'text-gray-700 hover:text-primary'}`}
                    onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
                    onMouseEnter={() => setOpenDropdown(key)}
                    aria-expanded={openDropdown === key}
                    aria-haspopup="true"
                  >
                    {dropdown.label}
                    <i className={`fas fa-chevron-down text-[10px] transition-transform duration-200 ${openDropdown === key ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown panel */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 transition-all duration-200 origin-top
                      ${openDropdown === key ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" />
                    {dropdown.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150
                          ${isActive(item.to)
                            ? 'bg-primary/5 text-primary font-semibold'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                          }`}
                      >
                        <i className={`fas ${item.icon} w-4 text-center text-primary/70`} />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              {navLink('/admission', 'Admission')}
              {navLink('/contact', 'Contact Us')}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/admission"
                className="bg-primary hover:bg-primary-dark text-white font-poppins font-600 text-sm px-5 py-2.5 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 whitespace-nowrap"
              >
                Apply Now
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden flex flex-col gap-1.5 p-2 -mr-2 cursor-pointer group"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300
                ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300
                ${menuOpen ? 'opacity-0 scale-x-0' : ''}`} />
              <span className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300
                ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden
          ${menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="border-t border-gray-100 bg-white px-4 py-4 space-y-1 max-h-[calc(100vh-70px)] overflow-y-auto">

            <MobileLink to="/" label="Home" isActive={isActive('/')} />
            <MobileLink to="/about" label="About Us" isActive={isActive('/about')} />

            {/* Mobile dropdowns */}
            {Object.entries(dropdowns).map(([key, dropdown]) => (
              <div key={key}>
                <button
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-gray-700 font-poppins font-600 text-sm hover:bg-gray-50"
                  onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
                >
                  {dropdown.label}
                  <i className={`fas fa-chevron-down text-xs transition-transform duration-200 ${openDropdown === key ? 'rotate-180 text-primary' : 'text-gray-400'}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openDropdown === key ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="pl-4 pb-2 space-y-1">
                    {dropdown.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors
                          ${isActive(item.to) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}
                      >
                        <i className={`fas ${item.icon} w-4 text-center text-primary/70`} />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            <MobileLink to="/admission" label="Admission" isActive={isActive('/admission')} />
            <MobileLink to="/contact" label="Contact Us" isActive={isActive('/contact')} />

            <div className="pt-2 pb-2">
              <Link
                to="/admission"
                className="flex items-center justify-center gap-2 w-full bg-primary text-white font-poppins font-600 text-sm py-3 rounded-xl hover:bg-primary-dark transition-colors"
              >
                <i className="fas fa-paper-plane" />
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Backdrop for dropdown (desktop) */}
      {openDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </>
  );
};

const MobileLink = ({ to, label, isActive }) => (
  <Link
    to={to}
    className={`flex items-center px-4 py-3 rounded-xl font-poppins font-600 text-sm transition-colors
      ${isActive ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'}`}
  >
    {label}
  </Link>
);

export default Navbar;
